import { ColumnDef } from "@tanstack/react-table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  ParamDataTable,
  dataTableSearchParsers,
  loadDataTableSearch,
} from "@/components/ui/param-data-table";
import {
  useGetBulkInvoices,
  getGetBulkInvoicesQueryOptions,
  exportBulkInvoices,
} from "@/requests/gen/react-query/bulk-invoice";
import { ExportCSV } from "@/components/ui/csv-export";
import { InvoiceTypeEnum } from "@/requests/gen/react-query/fastAPI.schemas";
import formatUSD from "@/lib/format-currency";
import { format, parseISO } from "date-fns";
import {
  createFileRoute,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";
import { keepPreviousData } from "@tanstack/react-query";
import { Header } from "@/components/universal/header";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useQueryStates, type inferParserType } from "nuqs";

type BulkInvoiceSearch = inferParserType<typeof dataTableSearchParsers>;

function bulkInvoiceQueryParams(
  p: BulkInvoiceSearch,
): Parameters<typeof getGetBulkInvoicesQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    created_at_start: p.created_at_start,
    created_at_end: p.created_at_end,
  };
}

export const Route = createFileRoute(
  "/_private/_bulk-invoice/bulk-invoice/lookup",
)({
  loader: ({ context: { queryClient }, location }) =>
    queryClient.prefetchQuery(
      getGetBulkInvoicesQueryOptions(
        bulkInvoiceQueryParams(loadDataTableSearch(location.searchStr)),
      ),
    ),
  component: BulkInvoiceLookupPage,
});

interface BulkInvoiceTableProps {
  link?: LinkProps;
  bulk_invoice_id: string;
  bulk_order_ids: string[];
  invoice_type: string;
  ordering_entity: string;
  payment_collector: string;
  total: string;
  due_at: string;
  charged: string;
  paid: string;
}

const columns: ColumnDef<BulkInvoiceTableProps>[] = [
  { header: "Invoice ID", accessorKey: "bulk_invoice_id" },
  {
    header: "Bulk Order ID",
    accessorKey: "bulk_order_ids",
    enableSorting: false,
    cell: (cell) => {
      const orderIds = cell.getValue() as string[];

      const firstOrderId = orderIds.at(0);
      const remainingCount = orderIds.length - 1;

      return (
        <div className="flex items-center gap-1">
          {firstOrderId}
          {remainingCount > 0 && (
            <Tooltip>
              <TooltipTrigger className="bg-primary rounded-full text-white size-5 flex items-center justify-center text-xs">
                +{remainingCount}
              </TooltipTrigger>
              <TooltipContent>{orderIds.slice(1).join(", ")}</TooltipContent>
            </Tooltip>
          )}
        </div>
      );
    },
  },
  { header: "Ordering Entity", accessorKey: "ordering_entity" },
  { header: "Payment Collector", accessorKey: "payment_collector" },
  { header: "Total", accessorKey: "total" },
  { header: "Due Date", accessorKey: "due_at" },
  { header: "Charged", accessorKey: "charged" },
  { header: "Paid", accessorKey: "paid" },
];

function BulkInvoiceLookupPage() {
  const [queryParams] = useQueryStates(dataTableSearchParsers);

  const bulk_invoices = useGetBulkInvoices(
    bulkInvoiceQueryParams(queryParams),
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  const tableData: BulkInvoiceTableProps[] | undefined =
    bulk_invoices.data?.items.map((invoice) => ({
      link: linkOptions({
        to: "/bulk-invoice/$bulkInvoiceId",
        params: { bulkInvoiceId: invoice.bulk_invoice_id },
      }),
      bulk_invoice_id: invoice.bulk_invoice_id,
      // Invoices can span multiple bulk orders; the first is shown with a +n
      // badge for any others
      bulk_order_ids: invoice.bulk_orders.map((order) => order.bulk_order_id),
      invoice_type:
        invoice.invoice_type === InvoiceTypeEnum.WHOLESALE
          ? "Wholesale"
          : "Standard",
      ordering_entity: invoice.ordering_entity.name,
      payment_collector: invoice.payment_collector.name,
      total: formatUSD(invoice.total),
      due_at: invoice.due_at
        ? format(parseISO(invoice.due_at), "MM-dd-yyyy")
        : "Pending fulfillment",
      charged: invoice.charged_at
        ? format(parseISO(invoice.charged_at), "MM-dd-yyyy")
        : "Uncharged",
      paid: invoice.paid_at
        ? format(parseISO(invoice.paid_at), "MM-dd-yyyy")
        : "Unpaid",
    }));

  return (
    <div className="text-left">
      <title>Invoices - Legrande Health Portal</title>
      <Header
        crumbs={[{ name: "Invoices", link: { to: "/bulk-invoice/lookup" } }]}
      />
      <Card>
        <CardHeader>
          <CardTitle>Invoices</CardTitle>
          <CardDescription>
            View invoices for your bulk orders. Click an invoice to see its
            contents.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={bulk_invoices.isFetching} />
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={bulk_invoices.isLoading}
            isError={bulk_invoices.isError}
            count={bulk_invoices.data?.count}
            footerActions={
              <ExportCSV
                onExport={() =>
                  exportBulkInvoices({
                    search: queryParams.search,
                    sort_column: queryParams.sort_column,
                    ascending: queryParams.ascending,
                    created_at_start: queryParams.created_at_start,
                    created_at_end: queryParams.created_at_end,
                    hidden_columns: queryParams.hidden,
                  })
                }
                filename="invoices"
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}
